import { Col, Input, Row, message } from "antd";
import React, { useEffect, useState } from "react";
import { pcidRecSearchHd, pcidRecSearchTb } from "../../services/api.js";

import CustomTable from "../../components/antdCustom/CustomTable";
import Title from "../../components/Custom/Title";
import useAPI from "../../hooks/useAPI";
import { MySubtitleH4 } from "../../styled/publicTag";

/**
 * 出貨記錄查詢頁面元件
 * 此元件包含兩個主要功能區塊：出貨記錄查詢和返修記錄查詢
 * 每個區塊都有資料列表顯示和詳細資訊顯示功能
 */
const OutStockRecord = () => {
  // ======== 狀態定義區域 ========

  // 出貨記錄表格資料
  const [s_outStockTableData, set_s_outStockTableData] = useState([]);
  // 返修記錄表格資料
  const [s_reWorkTableData, set_s_reWorkTableData] = useState([]);

  // 當前選中高亮顯示的出貨記錄行
  const [s_outStockLightData, set_s_outStockLightData] = useState({});
  // 當前選中高亮顯示的返修記錄行
  const [s_reWorkLightData, set_s_reWorkLightData] = useState({});

  // 出貨記錄查詢參數：搜索字串、頁碼、每頁筆數
  const [s_outStockSearchData, set_s_outStockSearchData] = useState({
    queryString: undefined,
    page: 1,
    pageSize: 100,
  });

  // 返修記錄查詢參數：搜索字串、頁碼、每頁筆數
  const [s_reWorkSearchData, set_s_reWorkSearchData] = useState({
    queryString: undefined,
    page: 1,
    pageSize: 100,
  });

  // 出貨記錄分頁資訊
  const [s_outStockPageInfo, set_s_outStockPageInfo] = useState({
    currentPage: 0,
    pageItemNumber: 0,
    totalItems: 0,
    pageSize: 0,
  });

  // 返修記錄分頁資訊
  const [s_reWorkPageInfo, set_s_reWorkPageInfo] = useState({
    currentPage: 0,
    pageItemNumber: 0,
    totalItems: 0,
    pageSize: 0,
  });

  // ======== API 調用及函數定義 ========

  /**
   * pcidRecSearchHd (表頭資料查詢) API 調用需要兩個參數:
   * 1. queryString - 搜索框輸入的值
   * 2. txt - 用於區分出貨(空值)和返修("reWoek")
   *
   * pcidRecSearchTb (表身資料查詢) API 調用需要選中行的pcid值
   */
  // 使用自定義Hook封裝出貨記錄表頭API調用
  const call_outStockPcidRecSearchHd = useAPI(pcidRecSearchHd);
  // 使用自定義Hook封裝出貨記錄表身API調用
  const call_outStockPcidRecSearchTb = useAPI(pcidRecSearchTb);
  // 使用自定義Hook封裝返修記錄表頭API調用
  const call_reWorkPcidRecSearchHd = useAPI(pcidRecSearchHd);
  // 使用自定義Hook封裝返修記錄表身API調用
  const call_reWorkPcidRecSearchTb = useAPI(pcidRecSearchTb);

  /**
   * 執行出貨記錄表頭資料查詢
   * 清空當前選中的高亮記錄
   */
  const onOutStockHdSearch = () => {
    call_outStockPcidRecSearchHd.request({
      ...s_outStockSearchData,
      txt: "", // 出貨記錄使用空值作為標識
    });

    set_s_outStockLightData({});
  };

  /**
   * 執行返修記錄表頭資料查詢
   * 清空當前選中的高亮記錄
   */
  const onReWorkHdSearch = () => {
    call_reWorkPcidRecSearchHd.request({
      ...s_reWorkSearchData,
      txt: "reWoek", // 返修記錄使用"reWoek"作為標識
    });

    set_s_reWorkLightData({});
  };

  // ======== 副作用處理區域 ========

  /**
   * 監聽出貨記錄表頭API回應狀態
   * 處理查詢成功或失敗的資料更新
   */
  useEffect(() => {
    // 查詢成功時更新表格資料與分頁資訊
    if (call_reWorkPcidRecSearchHd.status === "suc") {
      const tmpData =
        call_outStockPcidRecSearchHd?.data?.data?.map((item, index) => {
          return {
            ...item,
            key:
              index + s_outStockSearchData.pageSize * s_outStockSearchData.page,
          };
        }) || [];

      set_s_outStockTableData(tmpData);
      set_s_outStockPageInfo(call_outStockPcidRecSearchHd.data.pageInfo);
    }

    // 查詢失敗時重置資料與狀態
    if (call_outStockPcidRecSearchHd.status === "err") {
      set_s_outStockTableData([]);
      set_s_outStockPageInfo({
        currentPage: 0,
        pageItemNumber: 0,
        totalItems: 0,
        pageSize: 0,
      });
      set_s_outStockLightData({});
    }
  }, [call_outStockPcidRecSearchHd.status]);

  /**
   * 監聽出貨記錄頁碼或每頁筆數變更
   * 自動觸發查詢
   */
  useEffect(() => {
    onOutStockHdSearch();
  }, [s_outStockSearchData.page, s_outStockSearchData.pageSize]);

  /**
   * 監聽出貨記錄高亮選中項變更
   * 當選中項存在時查詢其詳細資料
   */
  useEffect(() => {
    if (Object.keys(s_outStockLightData).length > 0) {
      call_outStockPcidRecSearchTb.request({
        ...s_outStockLightData,
        txt: "", // 出貨記錄使用空值作為標識
      });
    }
  }, [s_outStockLightData]);

  useEffect(() => {
    if (call_outStockPcidRecSearchTb.status === "suc") {
      message.success(call_outStockPcidRecSearchTb.msg);
    }
    if (call_outStockPcidRecSearchTb.status === "err") {
      message.error(call_outStockPcidRecSearchTb.msg);
    }
  }, [call_outStockPcidRecSearchTb.status]);

  /**
   * 監聽返修記錄表頭API回應狀態
   * 處理查詢成功或失敗的資料更新
   */
  useEffect(() => {
    // 查詢成功時更新表格資料與分頁資訊
    if (call_reWorkPcidRecSearchHd.status === "suc") {
      const tmpData =
        call_reWorkPcidRecSearchHd?.data?.data?.map((item, index) => {
          return {
            ...item,
            key: index + s_reWorkSearchData.pageSize * s_reWorkSearchData.page,
          };
        }) || [];

      set_s_reWorkTableData(tmpData);
      set_s_reWorkPageInfo(call_reWorkPcidRecSearchHd.data.pageInfo);
    }

    // 查詢失敗時重置資料與狀態
    if (call_reWorkPcidRecSearchHd.status === "err") {
      set_s_reWorkTableData([]);
      set_s_reWorkPageInfo({
        currentPage: 0,
        pageItemNumber: 0,
        totalItems: 0,
        pageSize: 0,
      });
      set_s_reWorkLightData({});
    }
  }, [call_reWorkPcidRecSearchHd.status]);

  /**
   * 監聽返修記錄頁碼或每頁筆數變更
   * 自動觸發查詢
   */
  useEffect(() => {
    onReWorkHdSearch();
  }, [s_reWorkSearchData.page, s_reWorkSearchData.pageSize]);

  /**
   * 監聽返修記錄高亮選中項變更
   * 當選中項存在時查詢其詳細資料
   */
  useEffect(() => {
    if (Object.keys(s_reWorkLightData).length > 0) {
      call_reWorkPcidRecSearchTb.request({
        ...s_reWorkLightData,
        txt: "reWoek", // 返修記錄使用"reWoek"作為標識
      });
    }
  }, [s_reWorkLightData]);

  /**
   * 監聽返修記錄詳細資料查詢結果
   * 顯示成功或失敗提示訊息
   */
  useEffect(() => {
    if (call_reWorkPcidRecSearchTb.status === "suc") {
      message.success(call_reWorkPcidRecSearchTb.msg);
    }
    if (call_reWorkPcidRecSearchTb.status === "err") {
      message.error(call_reWorkPcidRecSearchTb.msg);
    }
  }, [call_reWorkPcidRecSearchTb.status]);

  /**
   * 表格欄位定義
   * 包含項目名稱、掃碼內容和生產序號三個欄位
   */
  const tableColumnsHd = [
    {
      title: "生產序號",
      dataIndex: "pcid",
    },
    {
      title: "掃碼內容",
      dataIndex: "itemcode",
    },
    {
      title: "項目名稱",
      dataIndex: "itemNM",
    },
  ];

  const tableColumnsTb = [
    {
      title: "工單",
      dataIndex: "operN",
      width: "25%",
    },
    {
      title: "生產序號",
      dataIndex: "pcid",
    },
    {
      title: "掃碼內容",
      dataIndex: "itemcode",
    },
    {
      title: "項目名稱",
      dataIndex: "itemNM",
    },
  ];

  // ======== JSX 渲染區域 ========

  return (
    <Row gutter={[12, 12]}>
      {/* 出貨記錄區塊 */}
      <Col span={24}>
        {/* 標題與搜索框 */}
        <Title title="出貨紀錄查詢">
          <Input.Search
            className="Input"
            placeholder="請輸入生產序號或序號"
            onSearch={(value) => {
              // 設置返修記錄搜索參數並執行查詢
              set_s_reWorkSearchData({
                ...s_reWorkSearchData,
                page: 1,
                queryString: value,
              });
              call_reWorkPcidRecSearchHd.request({
                ...s_reWorkSearchData,
                txt: "reWoek",
                page: 1,
                queryString: value,
              });

              // 設置出貨記錄搜索參數並執行查詢
              set_s_outStockSearchData({
                ...s_outStockSearchData,
                page: 1,
                queryString: value,
              });
              call_outStockPcidRecSearchHd.request({
                ...s_outStockSearchData,
                txt: "",
                page: 1,
                queryString: value,
              });

              // 清空選中狀態
              set_s_outStockLightData({});
              set_s_reWorkLightData({});
            }}
            enterButton
            allowClear
            style={{ width: "400px" }}
          />
        </Title>

        {/* 出貨記錄資料表格 */}
        <CustomTable
          scroll={{
            x: "max-content",
            y: (window.innerHeight - 60 - 45) / 4, // 動態計算表格高度
          }}
          columns={tableColumnsHd}
          dataSource={
            s_outStockTableData // 顯示全部資料
          }
          onRow={(record) => {
            return {
              onClick: () =>
                // 切換選中狀態：如果當前記錄已選中則取消選中，否則選中當前記錄
                record.key === s_outStockLightData.key
                  ? set_s_outStockLightData({})
                  : set_s_outStockLightData(record),
            };
          }}
          rowClassName={(record) => {
            // 為選中行添加高亮樣式
            return record.key === s_outStockLightData.key
              ? "clickRowStyle"
              : "";
          }}
          pagination={
            s_outStockLightData.key
              ? false // 選中單項時不顯示分頁
              : {
                  current: s_outStockSearchData.page,
                  total: s_outStockPageInfo.totalItems,
                  showSizeChanger: true,
                  pageSize: s_outStockSearchData.pageSize,
                  pageSizeOptions: ["100", "200", "300"],
                  onChange: (current, size) => {
                    // 分頁或每頁筆數變更處理
                    set_s_outStockSearchData({
                      ...s_outStockPageInfo,
                      pageSize: size,
                      page: current,
                    });
                  },
                  showTotal: (total, range) => (
                    <MySubtitleH4>
                      共 {total} 項 ， 正在顯示第{range[0]}到{range[1]}項
                    </MySubtitleH4>
                  ),
                }
          }
        />

        {/* 選中出貨記錄時顯示的詳細資訊區塊 */}
        {s_outStockLightData.key && (
          <>
            <Title title="出貨完整資訊"> </Title>
            <CustomTable
              scroll={{
                x: "max-content",
                y: (window.innerHeight - 60 - 45) / 5, // 動態計算表格高度
              }}
              columns={tableColumnsTb}
              dataSource={
                call_outStockPcidRecSearchTb?.data?.map((item, index) => {
                  return {
                    ...item,
                    key: index,
                  };
                }) || []
              }
              pagination={false} // 詳細資訊不使用分頁
            />
          </>
        )}
      </Col>

      {/* 返修記錄區塊 */}
      <Col span={24}>
        <Title title="返修紀錄查詢"> </Title>

        {/* 返修記錄資料表格 */}
        <CustomTable
          scroll={{
            x: "max-content",
            y: (window.innerHeight - 60 - 45) / 4, // 動態計算表格高度
          }}
          columns={tableColumnsHd}
          dataSource={
            s_reWorkTableData // 顯示全部資料
          }
          onRow={(record) => {
            return {
              onClick: () =>
                // 切換選中狀態：如果當前記錄已選中則取消選中，否則選中當前記錄
                record.key === s_reWorkLightData.key
                  ? set_s_reWorkLightData({})
                  : set_s_reWorkLightData(record),
            };
          }}
          rowClassName={(record) => {
            // 為選中行添加高亮樣式
            return record.key === s_reWorkLightData.key ? "clickRowStyle" : "";
          }}
          pagination={
            s_reWorkLightData.key
              ? false // 選中單項時不顯示分頁
              : {
                  current: s_reWorkSearchData.page,
                  total: s_reWorkPageInfo.totalItems,
                  showSizeChanger: true,
                  pageSize: s_reWorkSearchData.pageSize,
                  pageSizeOptions: ["100", "200", "300"],
                  onChange: (current, size) => {
                    // 分頁或每頁筆數變更處理
                    set_s_reWorkSearchData({
                      ...s_reWorkPageInfo,
                      pageSize: size,
                      page: current,
                    });
                  },
                  showTotal: (total, range) => (
                    <MySubtitleH4>
                      共 {total} 項 ， 正在顯示第{range[0]}到{range[1]}項
                    </MySubtitleH4>
                  ),
                }
          }
        />

        {/* 選中返修記錄時顯示的詳細資訊區塊 */}
        {s_reWorkLightData.key && (
          <>
            <Title title="返修完整資訊"> </Title>
            <CustomTable
              scroll={{
                x: "max-content",
                y: (window.innerHeight - 60 - 45) / 5, // 動態計算表格高度
              }}
              columns={tableColumnsTb}
              dataSource={
                call_reWorkPcidRecSearchTb?.data?.map((item, index) => {
                  return {
                    ...item,
                    key: index,
                  };
                }) || []
              }
              pagination={false} // 詳細資訊不使用分頁
            />
          </>
        )}
      </Col>
    </Row>
  );
};

export default OutStockRecord;
